import { Calendar, Search, AlertTriangle, Filter } from 'lucide-react'
import type { ReactNode, KeyboardEvent } from 'react'

/**
 * Empty state variant types
 */
export type FinancialEmptyStateVariant = 'no-data' | 'no-results' | 'error' | 'filter'

/**
 * Standard action handler types for common actions
 */
export type StandardActionType = 'openDatePicker' | 'clearFilters' | 'retry' | 'goBack'

/**
 * Props for the FinancialEmptyState component
 */
export interface FinancialEmptyStateProps {
  /** The variant determines the icon, default title, and description */
  variant: FinancialEmptyStateVariant
  /** Optional custom title to override variant default */
  title?: string
  /** Optional custom description to override variant default */
  description?: string
  /** Label for the primary action button */
  actionLabel?: string
  /** Callback when primary action button is clicked */
  onAction?: () => void
  /** Label for the secondary action button */
  secondaryActionLabel?: string
  /** Callback when secondary action button is clicked */
  onSecondaryAction?: () => void
  /** Additional CSS class name */
  className?: string
}

/**
 * Variant configuration with default values
 */
interface VariantConfig {
  icon: ReactNode
  iconBgColor: string
  iconColor: string
  defaultTitle: string
  defaultDescription: string
  defaultActionLabel: string
}

const VARIANT_CONFIGS: Record<FinancialEmptyStateVariant, VariantConfig> = {
  'no-data': {
    icon: <Calendar className="h-8 w-8" />,
    iconBgColor: 'bg-brand-primary/20',
    iconColor: 'text-brand-primary',
    defaultTitle: 'Sem dados disponíveis',
    defaultDescription: 'Não há dados para o período selecionado',
    defaultActionLabel: 'Selecionar outro período',
  },
  'no-results': {
    icon: <Search className="h-8 w-8" />,
    iconBgColor: 'bg-brand-blue/20',
    iconColor: 'text-brand-blue',
    defaultTitle: 'Nenhum resultado encontrado',
    defaultDescription: 'Sua busca não retornou resultados',
    defaultActionLabel: 'Limpar busca',
  },
  error: {
    icon: <AlertTriangle className="h-8 w-8" />,
    iconBgColor: 'bg-brand-red/20',
    iconColor: 'text-brand-red',
    defaultTitle: 'Erro ao carregar dados',
    defaultDescription: 'Ocorreu um erro ao carregar os dados',
    defaultActionLabel: 'Tentar novamente',
  },
  filter: {
    icon: <Filter className="h-8 w-8" />,
    iconBgColor: 'bg-yellow-400/20',
    iconColor: 'text-yellow-400',
    defaultTitle: 'Nenhum item corresponde aos filtros',
    defaultDescription: 'Ajuste os filtros para ver mais resultados',
    defaultActionLabel: 'Limpar filtros',
  },
}

/**
 * FinancialEmptyState Component
 *
 * A unified empty state component for the financial dashboard.
 * Supports four variants: no-data, no-results, error, and filter.
 * Each variant has appropriate default icons, titles, and descriptions.
 *
 * @example
 * // Basic usage with no-data variant
 * <FinancialEmptyState
 *   variant="no-data"
 *   onAction={() => openDatePicker()}
 * />
 *
 * @example
 * // Error variant with retry and go back actions
 * <FinancialEmptyState
 *   variant="error"
 *   title="Erro de conexão"
 *   description="Não foi possível conectar ao servidor"
 *   actionLabel="Tentar novamente"
 *   onAction={refetch}
 *   secondaryActionLabel="Voltar"
 *   onSecondaryAction={() => navigate(-1)}
 * />
 */
export function FinancialEmptyState({
  variant,
  title,
  description,
  actionLabel,
  onAction,
  secondaryActionLabel,
  onSecondaryAction,
  className = '',
}: FinancialEmptyStateProps): JSX.Element {
  const config = VARIANT_CONFIGS[variant]

  const displayTitle = title ?? config.defaultTitle
  const displayDescription = description ?? config.defaultDescription
  const displayActionLabel = actionLabel ?? config.defaultActionLabel

  const handleKeyDown = (callback?: () => void) => (event: KeyboardEvent): void => {
    if (callback && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault()
      callback()
    }
  }

  return (
    <div
      className={`bg-brand-dark-light/50 rounded-monumenta border border-gray-800/50 p-12 ${className}`}
      role="status"
      aria-live="polite"
    >
      <div className="flex flex-col items-center justify-center space-y-4 text-center">
        {/* Icon Container */}
        <div
          className={`w-16 h-16 ${config.iconBgColor} rounded-full flex items-center justify-center`}
          aria-hidden="true"
        >
          <span className={config.iconColor}>{config.icon}</span>
        </div>

        {/* Text Content */}
        <div className="space-y-2">
          <h3 className="text-xl font-bold text-white font-display">{displayTitle}</h3>
          <p className="text-brand-gray-light font-body max-w-md">{displayDescription}</p>
        </div>

        {/* Action Buttons */}
        {(onAction || onSecondaryAction) && (
          <div className="flex gap-3 pt-2" role="group" aria-label="Ações disponíveis">
            {onAction && (
              <button
                type="button"
                onClick={onAction}
                onKeyDown={handleKeyDown(onAction)}
                className="px-6 py-3 bg-brand-primary text-brand-dark rounded-monumenta font-display font-semibold hover:bg-brand-primary/90 focus:outline-none focus:ring-2 focus:ring-brand-primary focus:ring-offset-2 focus:ring-offset-brand-dark transition-all active:scale-95"
                aria-label={displayActionLabel}
              >
                {displayActionLabel}
              </button>
            )}
            {onSecondaryAction && secondaryActionLabel && (
              <button
                type="button"
                onClick={onSecondaryAction}
                onKeyDown={handleKeyDown(onSecondaryAction)}
                className="px-6 py-3 bg-transparent text-brand-gray-light border border-gray-700 rounded-monumenta font-display font-semibold hover:bg-brand-dark-lighter hover:text-white focus:outline-none focus:ring-2 focus:ring-brand-primary focus:ring-offset-2 focus:ring-offset-brand-dark transition-all active:scale-95"
                aria-label={secondaryActionLabel}
              >
                {secondaryActionLabel}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default FinancialEmptyState
